import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type HTMLAttributes,
  type MutableRefObject,
  type ReactNode,
  type Ref
} from "react";
import { cn } from "@/lib/cn";
import { createAgenticThemeVariables, type AgenticThemeOverrides } from "@/theme";
import { ensureDocumentStyles } from "@/shadow/shadowStyles";

export type BfrsColorScheme = "light" | "dark";

export type BfrsProviderProps = HTMLAttributes<HTMLDivElement> & {
  theme?: AgenticThemeOverrides;
  colorScheme?: BfrsColorScheme;
};

type BfrsStyleScopeContextValue = {
  root: HTMLElement | null;
  colorScheme?: BfrsColorScheme;
};

const BfrsStyleScopeContext = createContext<BfrsStyleScopeContextValue>({
  root: null
});

function assignRef<T>(ref: Ref<T> | undefined, value: T | null) {
  if (!ref) {
    return;
  }

  if (typeof ref === "function") {
    ref(value);
    return;
  }

  (ref as MutableRefObject<T | null>).current = value;
}

const toStyleProperties = (variables: Record<string, string | undefined>) => variables as CSSProperties;

const collectBfrsInlineStyle = (root: HTMLElement | null, colorScheme?: BfrsColorScheme): CSSProperties => {
  const style: Record<string, string> = {
    display: "contents"
  };

  if (colorScheme) {
    style.colorScheme = colorScheme;
  }

  if (!root) {
    return style as CSSProperties;
  }

  for (let index = 0; index < root.style.length; index += 1) {
    const name = root.style.item(index);

    if (name.startsWith("--bfrs-")) {
      style[name] = root.style.getPropertyValue(name);
    }
  }

  return style as CSSProperties;
};

export const BfrsProvider = forwardRef<HTMLDivElement, BfrsProviderProps>(({ className, colorScheme = "light", theme, style, ...props }, ref) => {
  const [root, setRoot] = useState<HTMLDivElement | null>(null);
  const themeStyle = useMemo(() => toStyleProperties(createAgenticThemeVariables(theme ?? {})), [theme]);
  const mergedStyle = useMemo(() => ({ ...themeStyle, ...style }), [style, themeStyle]);

  const handleRef = useCallback(
    (node: HTMLDivElement | null) => {
      setRoot(node);
      assignRef(ref, node);
    },
    [ref]
  );

  const contextValue = useMemo(
    () => ({
      root,
      colorScheme
    }),
    [colorScheme, root]
  );

  return (
    <BfrsStyleScopeContext.Provider value={contextValue}>
      <div
        ref={handleRef}
        data-bfrs-root=""
        data-bfrs-color-scheme={colorScheme}
        className={cn("bfrs", className)}
        style={mergedStyle}
        {...props}
      />
    </BfrsStyleScopeContext.Provider>
  );
});

BfrsProvider.displayName = "BfrsProvider";

export function BfrsStyleScope({ children, root, colorScheme = "light" }: { children: ReactNode; root: HTMLElement | null; colorScheme?: BfrsColorScheme }) {
  const contextValue = useMemo(
    () => ({
      root,
      colorScheme
    }),
    [colorScheme, root]
  );

  return <BfrsStyleScopeContext.Provider value={contextValue}>{children}</BfrsStyleScopeContext.Provider>;
}

export function BfrsPortalScope({ children }: { children: ReactNode }) {
  const { root, colorScheme } = useContext(BfrsStyleScopeContext);
  const [portalStyle, setPortalStyle] = useState<CSSProperties>(() => collectBfrsInlineStyle(root, colorScheme));

  useEffect(() => {
    // Overlays portal to <body> (outside any shadow root); make sure the document
    // carries the library stylesheet so the portaled content stays styled.
    ensureDocumentStyles();
    setPortalStyle(collectBfrsInlineStyle(root, colorScheme));

    if (!root || typeof globalThis.MutationObserver === "undefined") {
      return undefined;
    }

    const observer = new globalThis.MutationObserver(() => {
      setPortalStyle(collectBfrsInlineStyle(root, colorScheme));
    });

    observer.observe(root, {
      attributes: true,
      attributeFilter: ["style", "data-bfrs-color-scheme"]
    });

    return () => observer.disconnect();
  }, [colorScheme, root]);

  return (
    <div className="bfrs" data-bfrs-portal="" data-bfrs-color-scheme={colorScheme} style={portalStyle}>
      {children}
    </div>
  );
}
